import { useEffect, useState } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ShieldAlert } from "lucide-react";

interface LegalWarningModalProps {
  /** Stable scope key (e.g. course id or premium group id) — one acknowledgement per scope per session */
  scopeKey: string;
  /** Whether this warning is enabled by admin settings */
  enabled: boolean;
}

/**
 * Legal-deterrent modal shown once per session before protected playback begins.
 * Acknowledgement is stored in sessionStorage so it doesn't nag the same student
 * within a single browser tab/session, but reappears on a fresh visit.
 */
export const LegalWarningModal = ({ scopeKey, enabled }: LegalWarningModalProps) => {
  const storageKey = `fitawrari_legal_ack_${scopeKey}`;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!enabled || !scopeKey) return;
    try {
      const acked = sessionStorage.getItem(storageKey);
      if (!acked) setOpen(true);
    } catch { /* sessionStorage unavailable */ }
  }, [enabled, scopeKey, storageKey]);

  const handleAccept = () => {
    try { sessionStorage.setItem(storageKey, "1"); } catch { /* ignore */ }
    setOpen(false);
  };

  if (!enabled) return null;

  return (
    <AlertDialog open={open}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <div className="flex items-center gap-2 text-destructive">
            <ShieldAlert className="w-5 h-5" />
            <AlertDialogTitle>Protected Content — Legal Notice</AlertDialogTitle>
          </div>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-muted-foreground pt-2">
              <p>
                This material is the exclusive intellectual property of <strong>Fitawrari FX</strong>.
                Your session is fingerprinted and watermarked with your identity.
              </p>
              <p className="text-foreground font-medium">The following actions are strictly prohibited:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Recording, capturing, or screenshotting the video</li>
                <li>Sharing your account, login link, or device with another person</li>
                <li>Re-uploading, redistributing, or selling any portion of this content</li>
                <li>Using download tools, IDM, OBS, or screen-share software during playback</li>
              </ul>
              <p className="text-destructive text-xs font-medium pt-1">
                Violations are detected automatically and result in immediate, permanent loss of access — and may lead to legal action.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={handleAccept}>I understand & agree</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default LegalWarningModal;
